import { Image } from 'expo-image';
import { type ReactNode, useState } from 'react';
import { Pressable, View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { CarImages } from '@/api/cars/types';
import type { CarRating } from '@/api/partners/dashboard/use-car-ratings';
import type {
  DashboardReviewPeriod,
  LowRatedCar,
  LowRatedReview,
} from '@/api/partners/dashboard/use-dashboard-performance';
import { usePaginatedCarReviews } from '@/api/partners/dashboard/use-low-rated-car-reviews';
import { getCarImages } from '@/core';
import { Text } from '@/ui';
import { Chip, ChipType } from '@/ui/chip';
import { Icon } from '@/ui/icon';
import { ChevronDown } from '@/ui/icons/chevron-down';
import { ChevronUp } from '@/ui/icons/chevron-up';

import {
  DASHBOARD_RATING_BREAKDOWN_GOOD_THRESHOLD,
  DASHBOARD_RATING_CRITICAL_THRESHOLD,
  formatDashboardRatingScore,
  formatDashboardTripDate,
} from './dashboard-formatters';

/** Shared table for the Ratings tab preview and the full Car ratings page (`reviewScope`). */

type ColumnDef = {
  key: string;
  title: string;
  flex: number;
  minWidth: number;
  alignEnd?: boolean;
};

type CarRatingsTableCar = LowRatedCar | CarRating;

export type CarRatingsReviewScope = 'low-rated' | 'all';

function tableColumns(reviewScope: CarRatingsReviewScope): ColumnDef[] {
  return [
    { key: 'car', title: 'CAR', flex: 2.4, minWidth: 260 },
    { key: 'overall', title: 'OVERALL', flex: 0.85, minWidth: 80 },
    { key: 'pickup', title: 'PICKUP AND RETURN', flex: 1.1, minWidth: 108 },
    { key: 'condition', title: 'CAR CONDITION', flex: 1.1, minWidth: 108 },
    { key: 'cleanliness', title: 'CLEANLINESS', flex: 1, minWidth: 96 },
    {
      key: 'trips',
      title: reviewScope === 'all' ? 'RATED TRIPS' : 'LOW-RATED TRIPS',
      flex: 1.15,
      minWidth: 120,
    },
  ];
}

export type CarRatingsTableProps = {
  cars: CarRatingsTableCar[];
  period: DashboardReviewPeriod;
  onOpenReservation: (reservationId: number) => void;
  onViewAll?: () => void;
  reviewScope?: CarRatingsReviewScope;
};

function categoryScore(
  car: CarRatingsTableCar,
  key: 'pickupAndReturnRating' | 'carConditionRating' | 'cleanlinessRating'
) {
  return car[key];
}

function formatReviewScore(score: number | null) {
  if (score === null) {
    return 'Not rated';
  }

  return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

function parseCarDisplay(name: string) {
  const match = name.match(/^(.+?)\s*\(([^)]+)\)\s*$/);

  if (match) {
    return { title: match[1].trim(), subtitle: match[2].trim() };
  }

  return { title: name, subtitle: undefined };
}

function fallbackCarImageUrl() {
  return getCarImages(undefined as unknown as CarImages)[0]?.url ?? '';
}

function dashboardCarDisplay(car: CarRatingsTableCar) {
  if (car.brandName && car.modelName) {
    const yearPrefix = car.vehicleYear !== null ? `${car.vehicleYear} ` : '';

    return {
      title: `${yearPrefix}${car.brandName} ${car.modelName}`.trim(),
      subtitle: car.licensePlate || undefined,
    };
  }

  return parseCarDisplay(car.name);
}

function dashboardCarImageUrl(car: CarRatingsTableCar) {
  const url = car.imageUrl?.trim();

  return url || fallbackCarImageUrl();
}

function ratingChipType(score: number) {
  if (score < DASHBOARD_RATING_CRITICAL_THRESHOLD) {
    return ChipType.ERROR;
  }

  if (score < DASHBOARD_RATING_BREAKDOWN_GOOD_THRESHOLD) {
    return ChipType.WARNING;
  }

  return ChipType.SUCCESS;
}

function TableCell({
  column,
  children,
}: {
  column: ColumnDef;
  children: ReactNode;
}) {
  const { styles } = useStyles(stylesheet);

  return (
    <View
      style={[
        styles.tableCell,
        {
          flex: column.flex,
          minWidth: column.minWidth,
        },
        column.alignEnd ? styles.tableCellEnd : undefined,
        column.key === 'trips' ? styles.tripsTableCell : undefined,
      ]}
    >
      {children}
    </View>
  );
}

function LowRatedTripsValue({
  tripLabel,
  expanded,
}: {
  tripLabel: string;
  expanded: boolean;
}) {
  const { styles, theme } = useStyles(stylesheet);

  return (
    <View style={styles.tripsCell}>
      <Text type="bodyRegular">{tripLabel}</Text>
      <Icon
        type="naked"
        icon={expanded ? <ChevronUp /> : <ChevronDown />}
        fillColor={theme.colors.icon.color}
        width={10}
        height={6}
      />
    </View>
  );
}

function RatingScoreChip({
  score,
  variant = 'semantic',
}: {
  score: number | null;
  variant?: 'semantic' | 'neutral';
}) {
  const { styles } = useStyles(stylesheet);

  if (score === null) {
    return (
      <Text type="caption1" style={styles.secondary}>
        Not rated
      </Text>
    );
  }

  return (
    <Chip
      type={variant === 'neutral' ? ChipType.DEFAULT : ratingChipType(score)}
      text={formatDashboardRatingScore(score)}
      style={styles.ratingChip}
    />
  );
}

function CompactBreakdownRow({
  label,
  score,
}: {
  label: string;
  score: number | null;
}) {
  const { styles } = useStyles(stylesheet);

  return (
    <View style={styles.compactBreakdownRow}>
      <Text type="bodyRegular">{label}</Text>
      <RatingScoreChip score={score} />
    </View>
  );
}

function CompactLowRatedReviewRow({
  review,
  periodTimeZone,
  onOpenReservation,
}: {
  review: LowRatedReview;
  periodTimeZone?: string;
  onOpenReservation: (reservationId: number) => void;
}) {
  const { styles } = useStyles(stylesheet);

  return (
    <View style={styles.compactReviewRow}>
      <View style={styles.compactReviewCopy}>
        <Pressable onPress={() => onOpenReservation(review.reservationId)}>
          <Text type="caption1" style={styles.link}>
            {`Reservation ${review.reservationId}`}
          </Text>
        </Pressable>
        <Text type="caption1" style={styles.secondary}>
          {formatDashboardTripDate(review.tripDate, periodTimeZone)}
        </Text>
        <Text type="caption1" style={styles.secondary} numberOfLines={2}>
          {`Pickup and return ${formatReviewScore(
            review.pickupAndReturnRating
          )}  Car condition ${formatReviewScore(
            review.carConditionRating
          )}  Cleanliness ${formatReviewScore(review.cleanlinessRating)}`}
        </Text>
      </View>
      <RatingScoreChip score={review.score} variant="neutral" />
    </View>
  );
}

const REVIEW_ROW_COLUMNS = tableColumns('low-rated');

function LowRatedReviewRow({
  review,
  periodTimeZone,
  onOpenReservation,
}: {
  review: LowRatedReview;
  periodTimeZone?: string;
  onOpenReservation: (reservationId: number) => void;
}) {
  const { styles } = useStyles(stylesheet);

  return (
    <View style={styles.tableRow}>
      <TableCell column={REVIEW_ROW_COLUMNS[0]}>
        <Pressable onPress={() => onOpenReservation(review.reservationId)}>
          <Text type="bodyRegular" style={styles.link}>
            {`Reservation ${review.reservationId}`}
          </Text>
        </Pressable>
        <Text type="caption1" style={styles.secondary}>
          {formatDashboardTripDate(review.tripDate, periodTimeZone)}
        </Text>
      </TableCell>
      <TableCell column={REVIEW_ROW_COLUMNS[1]}>
        <RatingScoreChip score={review.score} variant="neutral" />
      </TableCell>
      <TableCell column={REVIEW_ROW_COLUMNS[2]}>
        <RatingScoreChip
          score={review.pickupAndReturnRating}
          variant="neutral"
        />
      </TableCell>
      <TableCell column={REVIEW_ROW_COLUMNS[3]}>
        <RatingScoreChip score={review.carConditionRating} variant="neutral" />
      </TableCell>
      <TableCell column={REVIEW_ROW_COLUMNS[4]}>
        <RatingScoreChip score={review.cleanlinessRating} variant="neutral" />
      </TableCell>
      <TableCell column={REVIEW_ROW_COLUMNS[5]}>{null}</TableCell>
    </View>
  );
}

type CarRatingsTableRowProps = {
  car: CarRatingsTableCar;
  period: DashboardReviewPeriod;
  reviewScope: CarRatingsReviewScope;
  expanded: boolean;
  onToggle: () => void;
  onOpenReservation: (reservationId: number) => void;
};

function CarRatingsTableRow({
  car,
  period,
  reviewScope,
  expanded,
  onToggle,
  onOpenReservation,
}: CarRatingsTableRowProps) {
  const { styles, theme } = useStyles(stylesheet);
  const isAllRatings = reviewScope === 'all';
  const {
    visibleReviews,
    totalCount,
    showMoreCount,
    canShowMore,
    isLoadingMore,
    isLoadingReviews,
    showMore,
  } = usePaginatedCarReviews({
    car,
    from: period.from,
    to: period.to,
    expanded,
    scope: isAllRatings ? 'all' : 'low-rated',
  });
  const imageUrl = dashboardCarImageUrl(car);
  const display = dashboardCarDisplay(car);
  const pickupAverage = categoryScore(car, 'pickupAndReturnRating');
  const conditionAverage = categoryScore(car, 'carConditionRating');
  const cleanlinessAverage = categoryScore(car, 'cleanlinessRating');
  const compactTripsLabel = isAllRatings
    ? totalCount === 1
      ? '1 rated trip'
      : `${totalCount.toLocaleString()} rated trips`
    : totalCount === 1
    ? '1 low-rated trip'
    : `${totalCount.toLocaleString()} low-rated trips`;
  const tripLabel =
    totalCount === 1 ? '1 trip' : `${totalCount.toLocaleString()} trips`;
  const expandedTripsTitle = isAllRatings ? 'Rated trips' : 'Low-rated trips';
  const isCompactLayout = ['xs', 'sm'].includes(UnistylesRuntime.breakpoint);
  const columns = tableColumns(reviewScope);

  if (isCompactLayout) {
    return (
      <View style={styles.carGroup}>
        <Pressable style={styles.compactCarRow} onPress={onToggle}>
          <View style={styles.compactCarMain}>
            <Image
              source={{ uri: imageUrl }}
              style={styles.carImage}
              contentFit="contain"
            />
            <View style={styles.carText}>
              <Text type="bodyRegular" numberOfLines={2}>
                {display.title}
              </Text>
              {display.subtitle ? (
                <Text type="caption1" style={styles.secondary}>
                  {display.subtitle}
                </Text>
              ) : null}
              <Text type="caption1" style={styles.secondary}>
                {compactTripsLabel}
              </Text>
            </View>
          </View>
          <RatingScoreChip score={car.averageScore} />
          <Icon
            type="naked"
            icon={expanded ? <ChevronUp /> : <ChevronDown />}
            fillColor={theme.colors.icon.color}
            width={10}
            height={6}
          />
        </Pressable>

        {expanded ? (
          <View style={styles.compactExpanded}>
            <CompactBreakdownRow
              label="Pickup and return"
              score={pickupAverage}
            />
            <CompactBreakdownRow
              label="Car condition"
              score={conditionAverage}
            />
            <CompactBreakdownRow
              label="Cleanliness"
              score={cleanlinessAverage}
            />
            <View style={styles.compactTripsBlock}>
              <View style={styles.compactTripsHeader}>
                <Text type="caption1" style={styles.secondary}>
                  {expandedTripsTitle}
                </Text>
              </View>
              {isLoadingReviews && visibleReviews.length === 0 ? (
                <Text type="caption1" style={styles.secondary}>
                  Loading trips…
                </Text>
              ) : null}
              {visibleReviews.map((review) => (
                <CompactLowRatedReviewRow
                  key={review.reservationId}
                  review={review}
                  periodTimeZone={period.timeZone}
                  onOpenReservation={onOpenReservation}
                />
              ))}
              {canShowMore ? (
                <Pressable
                  style={styles.showMore}
                  onPress={() => {
                    void showMore();
                  }}
                  disabled={isLoadingMore}
                >
                  <Text type="bodyRegular" style={styles.link}>
                    {isLoadingMore ? 'Loading…' : `Show ${showMoreCount} more`}
                  </Text>
                </Pressable>
              ) : null}
            </View>
          </View>
        ) : null}
      </View>
    );
  }

  return (
    <View style={styles.carGroup}>
      <Pressable style={styles.tableRow} onPress={onToggle}>
        <TableCell column={columns[0]}>
          <View style={styles.carIdentity}>
            <Image
              source={{ uri: imageUrl }}
              style={styles.carImage}
              contentFit="contain"
            />
            <View style={styles.carText}>
              <Text type="bodyRegular" numberOfLines={2}>
                {display.title}
              </Text>
              {display.subtitle ? (
                <Text type="caption1" style={styles.secondary}>
                  {display.subtitle}
                </Text>
              ) : null}
            </View>
          </View>
        </TableCell>
        <TableCell column={columns[1]}>
          <RatingScoreChip score={car.averageScore} />
        </TableCell>
        <TableCell column={columns[2]}>
          <RatingScoreChip score={pickupAverage} />
        </TableCell>
        <TableCell column={columns[3]}>
          <RatingScoreChip score={conditionAverage} />
        </TableCell>
        <TableCell column={columns[4]}>
          <RatingScoreChip score={cleanlinessAverage} />
        </TableCell>
        <TableCell column={columns[5]}>
          <LowRatedTripsValue tripLabel={tripLabel} expanded={expanded} />
        </TableCell>
      </Pressable>

      {expanded ? (
        <View style={styles.drawer}>
          {isLoadingReviews && visibleReviews.length === 0 ? (
            <Text type="caption1" style={styles.secondary}>
              Loading trips…
            </Text>
          ) : null}
          {visibleReviews.map((review) => (
            <LowRatedReviewRow
              key={review.reservationId}
              review={review}
              periodTimeZone={period.timeZone}
              onOpenReservation={onOpenReservation}
            />
          ))}
          {canShowMore ? (
            <Pressable
              style={styles.showMore}
              onPress={() => {
                void showMore();
              }}
              disabled={isLoadingMore}
            >
              <Text type="bodyRegular" style={styles.link}>
                {isLoadingMore ? 'Loading…' : `Show ${showMoreCount} more`}
              </Text>
            </Pressable>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

export function CarRatingsTable({
  cars,
  period,
  onViewAll,
  onOpenReservation,
  reviewScope = 'low-rated',
}: CarRatingsTableProps) {
  const { styles } = useStyles(stylesheet);
  const isCompactLayout = ['xs', 'sm'].includes(UnistylesRuntime.breakpoint);
  const [expandedCarId, setExpandedCarId] = useState<number | null>(null);
  const columns = tableColumns(reviewScope);
  const showDashboardSection = onViewAll !== undefined;

  return (
    <View
      style={showDashboardSection ? styles.section : styles.embeddedSection}
    >
      {showDashboardSection ? (
        <View style={styles.header}>
          <View style={styles.titles}>
            <Text type={isCompactLayout ? 'title2' : 'bodyBold'}>
              Cars With Low Ratings
            </Text>
            <Text type="caption1" style={styles.secondary}>
              Average rating of 3 or lower
            </Text>
          </View>
          <Pressable onPress={onViewAll}>
            <Text type="bodyRegular" style={styles.link}>
              View all ratings
            </Text>
          </Pressable>
        </View>
      ) : null}

      <View style={styles.tableShell}>
        {isCompactLayout ? null : (
          <View style={styles.tableRow}>
            {columns.map((column) => (
              <TableCell key={column.key} column={column}>
                <Text
                  type="caption1"
                  style={styles.headerLabel}
                  numberOfLines={2}
                >
                  {column.title}
                </Text>
              </TableCell>
            ))}
          </View>
        )}

        {cars.map((car) => (
          <CarRatingsTableRow
            key={car.id}
            car={car}
            reviewScope={reviewScope}
            period={period}
            expanded={expandedCarId === car.id}
            onToggle={() => {
              setExpandedCarId((current) =>
                current === car.id ? null : car.id
              );
            }}
            onOpenReservation={onOpenReservation}
          />
        ))}
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  section: {
    gap: 16,
    marginTop: 24,
  },
  embeddedSection: {
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
  },
  titles: {
    flex: 1,
    gap: 4,
  },
  secondary: {
    color: theme.colors.text.secondary,
  },
  link: {
    textDecorationLine: 'underline',
  },
  tableShell: {
    width: '100%',
    borderWidth: 1,
    borderColor: theme.colors.card.default.borderColor,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: theme.colors.background_1,
  },
  tableRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  headerLabel: {
    color: theme.colors.text.secondary,
  },
  tableCell: {
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  tableCellEnd: {
    alignItems: 'flex-end',
  },
  tripsTableCell: {
    alignSelf: 'stretch',
  },
  tripsCell: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    alignSelf: 'stretch',
    width: '100%',
  },
  carGroup: {
    width: '100%',
  },
  carIdentity: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  carImage: {
    width: 75,
    height: 50,
  },
  carText: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  drawer: {
    backgroundColor: theme.colors.background_2,
  },
  showMore: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  ratingChip: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    minWidth: 44,
  },
  compactCarRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  compactCarMain: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
  },
  compactExpanded: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  compactTripsBlock: {
    backgroundColor: theme.colors.background_2,
  },
  compactBreakdownRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  compactTripsHeader: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  compactReviewRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  compactReviewCopy: {
    flex: 1,
    gap: 4,
    minWidth: 0,
  },
}));
